import { NetworkStatus } from '@apollo/client'
import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { sortList } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import React, {
    MutableRefObject,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Row, SortingRule } from 'react-table'

import { SpecTable } from 'bpm/components/SpecTable'
import { SpecTableTitleSettingsProps } from 'bpm/components/SpecTable/SpecTableTitleSettings'

import { PublishingAuthor } from '@deltek/specification-client/core/Configurations/Authorization'
import { ContentLibrary } from '@deltek/specification-client/core/Models/product'
import {
    ActionColumnProps,
    SpecFilters,
    SpecIdType,
} from 'bpm/components/SpecTable/Spectable.types'
import { IdTitleProps } from 'bpm/components/SpecTable/settings/TableSettings'
import TableLoadingSkeleton from 'bpm/components/SpecTable/tableBody/TableLoadingSkeleton'
import useSpecTableOptions from 'bpm/components/SpecTable/useSpecTableOptions'
import {
    TVColumn,
    TVData,
} from '../ProductCardManagementPage/ProductCardManagementPage.types'
import useListingQueryHelper from '../ProductCardManagementPage/ProductListingsPage/useListingQueryHelper'
import InfiniteLoader, { InfiniteLoaderProps } from './InfiniteLoader'
import RowActionButton from './RowActionButton'
import useTablePageStore from './useTablePageStores'

const skeletonItemHeight = 50

export const hiddenColumns: SpecIdType<ListingMatch>[] = [
    'videos',
    'images',
    'manufacturerDocs',
    'catalogs',
    'bimLinks',
]

export const prepareOrderByString = (
    newOrderByObject: SortingRule<ListingMatch>[]
) => {
    const orderByString = newOrderByObject
        .map((sort) => {
            const descString = sort.desc ? ' desc' : ''
            return `${sort.id}${descString}`
        })
        .join(',')

    return orderByString
}

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        position: 'relative',
        bottom: pxToRem(50),
    },
}))

interface TablePageProps {
    tableData?: Partial<TVData>[]
    fetchMoreTableData: any
    tableDataNetworkStatus: NetworkStatus
    totalItems: number
    showFiltersRow: boolean
    columns: TVColumn[]
    hiddenColumns: string[]
    groupBy?: SpecIdType<TVData>[]
    fetchDataLoading: boolean
    fetchNextDataLoading: boolean
    defaultExpandedRows?: Record<string, boolean>
    autoResetExpanded?: boolean
    tableRef?: MutableRefObject<any>
    showBottomLoadingIndicator?: boolean
    setAutoResetExpanded?: (value: boolean) => void
    resetFetch: () => void
    isAdminAccess?: boolean
}

const TablePage: React.FC<TablePageProps> = ({
    tableData,
    fetchMoreTableData,
    totalItems,
    showFiltersRow,
    columns,
    hiddenColumns,
    defaultExpandedRows,
    autoResetExpanded,
    tableRef,
    fetchDataLoading,
    fetchNextDataLoading,
    showBottomLoadingIndicator,
    setAutoResetExpanded,
    resetFetch,
    isAdminAccess,
}) => {
    const classes = useStyles()

    const [rows, setRows] = useState<Partial<ListingMatch>[] | undefined>(
        tableData
    )

    const { saveTableViewSettings } = useListingQueryHelper()

    const {
        columnWidths,
        hasNextPage,
        setShowBulkActions,
        setEnableBulkActions,
        setCopyMoveParams,
        pinningProps,
        filterState,
        groupColumns,
        orderByState,
        setColumnWidths,
        setOrderBy,
        setFilters,
        selectedRowIds,
        setSelectedRowIds,
    } = useTablePageStore()

    const groupBy = useMemo(
        () => groupColumns.map((group: IdTitleProps) => group.id),
        [groupColumns]
    )

    const sortBy = useMemo(() => {
        if (!!orderByState) {
            let newOrderBy: SortingRule<ListingMatch>[] = []
            orderByState.split(',').forEach((sort: string) => {
                const order = sort.split(' ')
                if (!!order[0]) {
                    newOrderBy.push({ id: order[0], desc: !!order[1] })
                }
            })
            return newOrderBy
        }
        return []
    }, [orderByState])

    // Spectable settings
    const [isUpdated, setIsUpdated] = useState(true)
    const [expanded, setExpanded] = useState<
        Record<SpecIdType<ListingMatch>, boolean>
    >(defaultExpandedRows ?? {})

    const prevDefaultExpanded: MutableRefObject<
        Record<string, boolean> | undefined
    > = useRef(defaultExpandedRows)

    const divContainer = useRef<HTMLDivElement>()

    const previousColLength = usePrevious(columns?.length)
    const prevSortBy: MutableRefObject<SortingRule<ListingMatch>[]> = useRef([])
    const prevFilters: MutableRefObject<SpecFilters<ListingMatch>> = useRef(
        filterState
    )
    const prevTableData = usePrevious(tableData)

    const tableHeight = useMemo(() => {
        // we hard coded this since the only reliable source of the height is the innerheight minus 220 which is the height of the menu
        return Number(window.innerHeight - 220)
    }, [window.innerHeight])

    const fetchNextData = () => {
        fetchMoreTableData()
    }

    // This is how you will add your own custom action component.
    // From here, you will get a "cell" prop that contains the entire row/data
    // for that row.  So, if you have the id as hidden, you can get it by calling
    // cell.row.original.
    const actionColumnProps: ActionColumnProps<ListingMatch> = {
        Cell: ({ cell }) => {
            return cell?.row?.isGrouped ? (
                <></>
            ) : (
                <RowActionButton cell={cell} isAdminAccess={isAdminAccess} />
            )
        },
        width: 60,
    }

    const filterRows = useCallback(
        (dataToUse: ListingMatch[]) => {
            prevFilters.current = filterState ?? []

            const nonEmptyFilter = filterState?.filter(
                (filter: Record<string, string | string[]>) =>
                    !!filter.value?.length
            )

            return dataToUse?.filter((row: ListingMatch) => {
                return nonEmptyFilter?.every(
                    (filter: { id: string; value: any }) => {
                        const keyName = filter?.id as keyof ListingMatch
                        if (
                            Array.isArray(row[keyName]) &&
                            keyName === 'categories'
                        ) {
                            if (
                                !(row[keyName] as string[])
                                    ?.join(', ')
                                    ?.toLowerCase()
                                    .includes(filter?.value?.toLowerCase())
                            ) {
                                return false
                            }
                        } else if (keyName === 'isActive') {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter === 'Active' &&
                                                !row[keyName]) ||
                                            (statusFilter === 'Inactive' &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (
                            keyName === 'type' ||
                            keyName === 'submissionStatus'
                        ) {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (statusFilter !== row[keyName]) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (
                            keyName === 'canAutoGenerateProductSpecification'
                        ) {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter ===
                                                'Can auto generate' &&
                                                !row[keyName]) ||
                                            (statusFilter ===
                                                "Can't auto generate" &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (keyName === 'displayNameWithFirmName') {
                            const actualString = row[keyName]
                                ?.split('%|%')
                                ?.map((val) => val?.replaceAll("'", ''))
                            const formattedString =
                                actualString?.[0] !== 'null'
                                    ? `${actualString?.[0]} (${actualString?.[1]})`
                                    : `${actualString?.[1]}`
                            return formattedString
                                ?.toLowerCase()
                                .trim()
                                .includes(filter?.value?.trim().toLowerCase())
                        } else if (keyName === 'productName') {
                            const actualString = !row[keyName]
                                ? 'No product name'
                                : row[keyName]
                            return actualString
                                ?.toLowerCase()
                                .includes(filter?.value?.trim().toLowerCase())
                        } else if (keyName === 'specAgentProductTypeId') {
                            const actualString = String(row[keyName])
                            return actualString.includes(filter?.value?.trim())
                        } else if (keyName === 'hasManufacturerOptions') {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter === 'True' &&
                                                !row[keyName]) ||
                                            (statusFilter === 'False' &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (keyName === 'hasProductDetailNotes') {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter === 'Has Notes' &&
                                                !row[keyName]) ||
                                            (statusFilter ===
                                                'None Available' &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (keyName === 'contentLibrary') {
                            if (
                                filter?.value.includes(
                                    ContentLibrary[
                                        row[
                                            keyName
                                        ] as keyof typeof ContentLibrary
                                    ]
                                )
                            )
                                return true
                            else return false
                        } else if (
                            !(row[keyName] as string)
                                ?.toLowerCase()
                                .includes(filter?.value?.trim().toLowerCase())
                        ) {
                            return false
                        }
                        return true
                    }
                )
            })
        },
        [filterState, prevFilters]
    )

    const orderRows = useCallback(
        (dataToUse: ListingMatch[]) => {
            return sortList(
                [...(dataToUse ?? [])],
                orderByState.replace(/ asc/g, '')
            )
        },
        [orderByState]
    )

    const handleEnableBulkActions = useCallback(
        (selectedRowIds) => {
            const selectedRows = Object.keys(selectedRowIds || {})
                .map((index) => ({
                    ...specTableProps.tableOptions.data[Number(index)],
                    index,
                }))
                .filter(
                    (row: Partial<ListingMatch & { index: string }>) =>
                        row?.submissionStatus !== 'Deprecated'
                )

            const filteredSelectedRows = selectedRows.reduce(
                (
                    acc: Record<string, boolean>,
                    curr: Partial<ListingMatch & { index: string }>
                ) => {
                    acc[curr.index ?? ''] = true
                    return acc
                },
                {}
            )

            setSelectedRowIds(filteredSelectedRows)

            const enableBulkActions = [
                ...new Set(
                    selectedRows
                        .map(
                            (values: any) =>
                                `${values?.sectionName}${values?.productType}`
                        )
                        .filter((value) => !!value)
                ),
            ]

            const selectedRowObject: any = selectedRows[0]

            setEnableBulkActions?.(
                enableBulkActions.length === 1 &&
                    specTableProps.userRole !== PublishingAuthor
            )

            if (enableBulkActions.length === 1) {
                setCopyMoveParams?.({
                    sectionName: selectedRowObject?.sectionName,
                    productType: selectedRowObject?.productType,
                    selectedRows,
                })
            } else {
                setCopyMoveParams?.({})
            }
        },
        [rows, setCopyMoveParams, setEnableBulkActions]
    )

    const stateCallback = useCallback(
        (state: any) => {
            if (!state.columnResizing.isResizingColumn) {
                let isTempUpdated = true
                const newFilters = state?.filters?.filter(
                    (filter: { key: string; value: any }) => {
                        return !isEqual(
                            prevFilters?.current?.[
                                prevFilters?.current.findIndex(
                                    (inFilter) => inFilter.id === filter.key
                                )
                            ]?.value ?? '',
                            filter.value
                        )
                    }
                )

                const hasFilterChanges = !isEqual(filterState, newFilters)
                const hasSortingChanges = !isEqual(
                    prevSortBy.current,
                    state.sortBy
                )

                if (Object.keys(state.columnResizing.columnWidths).length) {
                    setColumnWidths(
                        state.columnResizing.columnWidths,
                        saveTableViewSettings
                    )
                }

                if (!isEqual(expanded, state?.expanded)) {
                    isTempUpdated = false
                    setExpanded(state?.expanded)
                }

                if (!isEqual(prevSortBy.current, state?.sortBy)) {
                    isTempUpdated = false
                    setOrderBy(
                        prepareOrderByString(state?.sortBy),
                        saveTableViewSettings
                    )
                    prevSortBy.current = state?.sortBy
                    tableRef?.current?.resetSelectedRows?.()
                }

                setFilters?.(newFilters)

                if (hasFilterChanges || hasSortingChanges) {
                    isTempUpdated = false
                    tableRef?.current?.resetSelectedRows?.()
                    if (setAutoResetExpanded) {
                        setAutoResetExpanded(false)
                    }
                }

                if (!isTempUpdated) {
                    setIsUpdated(false)
                }

                if (!isEqual(state?.selectedRowIds, selectedRowIds)) {
                    handleEnableBulkActions(state?.selectedRowIds)
                }
            }
        },
        [
            tableData,
            filterState,
            setOrderBy,
            setFilters,
            setColumnWidths,
            saveTableViewSettings,
            selectedRowIds,
            tableRef?.current,
        ]
    )

    const groupColumnWidth = useMemo(() => {
        if (columnWidths?.groupColExpander) {
            return columnWidths?.groupColExpander
        }
        return 300
    }, [columnWidths])

    useEffect(() => {
        // set expanded if there are changes on the defaultexpandedrows since this will come from a new listing fetch
        if (
            !!defaultExpandedRows &&
            !isEqual(prevDefaultExpanded?.current, defaultExpandedRows)
        ) {
            setExpanded(defaultExpandedRows ?? {})
            setIsUpdated(false)
        }
    }, [defaultExpandedRows])

    const tableProps = useMemo(() => {
        return {
            actionColumnProps,
            allowGrouping: true,
            data: (rows as ListingMatch[]) ?? [],
            divContainerRef: divContainer,
            columns,
            expanded,
            fetchData: resetFetch,
            fetchDataLoading,
            fetchNextData,
            fetchNextDataLoading,
            filters: filterState,
            groupBy,
            groupColumnWidth,
            height: tableHeight,
            hasNextPage: hasNextPage,
            hiddenColumns,
            onStateChange: stateCallback,
            sortBy,
            pageSize: 150,
            showCheckboxColumn: true,
            defaultSortCount: 2,
            inverseColumns: ['isActive', 'canAutoGenerateProductSpecification'],
            specTableTitleSettings: {
                showFiltersRow: columns.length > 0 && showFiltersRow,
                showTableSettings: false,
                theme: 'light',
                hasGrouping: true,
                tableSettings: {
                    initialPinningProps: pinningProps,
                    maxGroupCount: 3,
                    showGroupOptionSetting: true,
                    showPinning: true,
                    canToggleGroup: true,
                },
                onFilterClick: () => {},
            } as SpecTableTitleSettingsProps<ListingMatch>,
            totalItems,
            noDataComponent: columns.length > 0 && <></>,
            autoResetExpanded,
            showBottomLoadingIndicator,
            setEnableBulkActions,
            setCopyMoveParams,
            selectedRowIds,
            getDisabledCheckbox: (row: Row<any>) => {
                return row?.original?.submissionStatus === 'Deprecated'
            },
            checkboxDisabledTooltip:
                'This product is deprecated and cannot be selected for actions.',
        }
    }, [
        actionColumnProps,
        rows,
        columns,
        expanded,
        filterState,
        resetFetch,
        fetchNextData,
        groupBy,
        groupColumnWidth,
        hasNextPage,
        sortBy,
        stateCallback,
        showFiltersRow,
        totalItems,
        defaultExpandedRows,
        autoResetExpanded,
        fetchDataLoading,
        fetchNextDataLoading,
        showBottomLoadingIndicator,
        setEnableBulkActions,
        setCopyMoveParams,
        selectedRowIds,
    ])

    /**
     * useSpecTableOptions give you insight of all the exposed props that you can
     * do within the SpecTable.
     */
    const specTableProps = useSpecTableOptions<TVData>(tableProps)

    useEffect(() => {
        if (!isUpdated || !isEqual(tableData, prevTableData)) {
            let newRows = tableData as TVData[]
            newRows = filterRows(newRows)
            newRows = orderRows(newRows)
            setRows(newRows)
            setIsUpdated(true)
        }
    }, [
        tableData,
        filterState,
        filterRows,
        orderByState,
        orderRows,
        columns,
        previousColLength,
        rows,
        defaultExpandedRows,
        expanded, // update here if expanded is updated
    ])

    useEffect(() => {
        setShowBulkActions(true)
    }, [])

    const InfiniteLoaderComponent = memo(
        (props: Partial<InfiniteLoaderProps>) => {
            const containerWidth = divContainer?.current?.getBoundingClientRect()
                .width

            return (
                <InfiniteLoader
                    totalItems={totalItems}
                    itemLength={tableData?.length}
                    containerWidth={containerWidth}
                    {...props}
                />
            )
        }
    )

    const SkeletonComponent = memo(
        ({
            fetchDataLoading,
            skeletonItemHeight = 50,
        }: {
            fetchDataLoading?: boolean
            skeletonItemHeight?: number
        }) => {
            return !!fetchDataLoading ? (
                <TableLoadingSkeleton
                    height={skeletonItemHeight}
                    pageSize={50}
                />
            ) : (
                <></>
            )
        }
    )

    return (
        <GridContainer
            className={classes.container}
            data-testid="tableContainer"
        >
            <GridItem xs={12} ref={divContainer}>
                <SpecTable
                    {...specTableProps}
                    showCheckboxColumn={!!isAdminAccess}
                    showTVSettings={true}
                    lazyLoading={true}
                    tableMinWidth={`${pxToRem(1920)} !important`}
                    groupedCellHeight={33}
                    childRef={tableRef}
                    sortBy={orderByState}
                    LoadNextComponent={
                        <InfiniteLoaderComponent
                            isLoading={
                                !!fetchDataLoading || !!fetchNextDataLoading
                            }
                            hasNextPage={!!hasNextPage}
                            fetchNextPage={fetchNextData}
                            skeletonItemHeight={skeletonItemHeight}
                        />
                    }
                />
            </GridItem>
        </GridContainer>
    )
}

export default TablePage
